import { useLayoutEffect, useState } from 'react';

// Matches the expanded XtramModal max-height (96vh) and the report frame's padding + border.
const MODAL_MAX_VH = 0.96;
const FRAME_CHROME = 24;

/**
 * Scales a fixed-width report (the `.container` inside `contentRef`) to the frame's width and to the height an
 * expanded XtramModal has left at its maximum size, so the whole report shows without scrolling.
 * Returns the scale for the report's CSS variable and the height to give the frame.
 * `watchRefs` are the blocks above the frame whose size changes the room left for it.
 */
export default function useReportFit({ open, frameRef, contentRef, watchRefs = [], reportWidth = 1288, deps = [] }) {
    const [fit, setFit] = useState({ scale: 0.6, height: null });

    useLayoutEffect(() => {
        const frame = frameRef.current;
        if (!open || !frame) return undefined;

        const measure = () => {
            const report = contentRef.current?.querySelector('.container');
            const scroller = frame.closest('[data-xtram-scroll]');
            const modalBody = scroller?.parentElement;
            let scale = frame.clientWidth / reportWidth;

            if (report && scroller && modalBody) {
                const maxBodyHeight = Math.min(window.innerHeight * MODAL_MAX_VH, window.innerHeight - 24) - 2;
                const chrome = modalBody.offsetHeight - scroller.clientHeight;
                const above = frame.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
                const available = maxBodyHeight - chrome - above - FRAME_CHROME;
                scale = Math.min(scale, available / report.offsetHeight);
            }

            scale = Math.min(1, Math.max(0.3, scale));
            const height = report ? Math.ceil(report.offsetHeight * scale) : null;
            setFit((prev) => (prev.scale === scale && prev.height === height ? prev : { scale, height }));
        };

        measure();
        const observer = new ResizeObserver(measure);
        [frame, ...watchRefs.map((ref) => ref.current)].forEach((el) => el && observer.observe(el));
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [open, ...deps]);

    return fit;
}
